import type {MouseEvent} from 'react';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {IconButton, InputAdornment, Tooltip} from '@mui/material';
import {assert} from 'assert-ts';
import type {
  Concept,
  LinkTargetMainEntityType,
  LinkTargetSubEntityType,
} from 'types';
import type {FieldExtensionUnsubscribe as LinkedSearchUnsubscribe} from 'sceneExtensions/types';
import type {
  EmptyLinkedValueLink,
  FieldRequiredSimple,
  LinkedValueLink,
  UnnamedLinkedValueLink,
  UnverifiedLinkedValueLink,
  VerifiedLinkedValueLink,
} from 'schemaDefinition/types';
import {useLinkedValueSearchFieldContext} from 'sceneExtensions/extensions/linkedValueSearch/hooks/useLinkedValueSearchFieldContext';
import type {TextFieldProps} from '../types';
import type {BaseFieldProps} from './types';
import {Icon} from '../icons';
import {Layout} from '../layout';
import {FieldLabel} from './FieldLabel';
import {LinkStatusIcon} from './LinkStatusIcon';
import './LinkedField.scss';
import {NullFieldPlaceholder} from './NullFieldPlaceholder';
import {MuiTextFieldStyled} from './muiStyled';

type Props = BaseFieldProps &
  Pick<TextFieldProps, 'placeholder'> &
  Required<Pick<TextFieldProps, 'name'>> & {
    /** Displays what field is being edited via the right panel */
    focusableId?: string;
    link?: LinkedValueLink;
    /** Default true */
    linkable?: boolean;
    formattedValue: string;
    required?: FieldRequiredSimple;
    searchTitleKey?: string;
    entity: LinkTargetMainEntityType | undefined;
    entitySubtypes?: LinkTargetSubEntityType[] | undefined;
    allowedEntities?: Concept[] | undefined;
    onChange: (linkedValue: VerifiedLinkedValueLink | undefined) => void;
    onEdit?: (
      linkedValue:
        | UnverifiedLinkedValueLink
        | UnnamedLinkedValueLink
        | EmptyLinkedValueLink,
    ) => void;
    onBlur?: () => void;
  };

export const LinkedField: React.FC<Props> = ({
  focusableId,
  label,
  name,
  required = false,
  error = false,
  readonly,
  showWhenReadonlyAndEmpty = false,
  searchTitleKey,
  entity,
  entitySubtypes,
  allowedEntities,
  link,
  linkable = true,
  formattedValue,
  onChange,
  onEdit,
  onBlur,
  layout,
  ...props
}) => {
  const {sourceField, setSourceField} = useLinkedValueSearchFieldContext();
  const ref = useRef<HTMLInputElement>(null);

  const [isDirectEditMode, setIsDirectEditMode] = useState<boolean>(
    link?.linkStatus === 'unnamed',
  );

  const unsubscribeRef = useRef<LinkedSearchUnsubscribe>(undefined);

  const handleSearch = useCallback(() => {
    if (!linkable) return;

    setSourceField(
      {
        fieldId: name,
        fieldConfig: {
          titleKey: searchTitleKey,
          canLink: !readonly,
          entity,
          entitySubtypes,
          allowedEntities,
        },
        currentValue: link,
      },
      val => {
        onChange(val);
        unsubscribeRef.current && unsubscribeRef.current(name);
        unsubscribeRef.current = undefined;
      },
      () => {
        unsubscribeRef.current = undefined;
      },
    ); //.then(unsubscribe => (unsubscribeRef.current = unsubscribe));
  }, [
    allowedEntities,
    entity,
    entitySubtypes,
    link,
    linkable,
    name,
    onChange,
    readonly,
    searchTitleKey,
    setSourceField,
  ]);

  const handleDirectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const edit = assert(onEdit, 'handleDirectChange: onEdit expected');
      const value = e.target.value;

      // Unnamed links remain unnamed
      if (link?.linkStatus === 'unnamed') {
        edit({...link, name: value});
      } else if (value) {
        const unverified: UnverifiedLinkedValueLink = {
          linkStatus: 'unverified',
          name: value,
        };
        edit(unverified);
      } else {
        const empty: EmptyLinkedValueLink = {
          linkStatus: 'empty',
        };
        edit(empty);
      }
    },
    [link, onEdit],
  );

  const handleToggleDirectEditMode = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDirectEditMode(prev => !prev);
    ref.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!linkable) return;

      if (e.code !== 'Enter' && e.code !== 'Space') return;

      handleSearch();
      e.preventDefault();
    },
    [handleSearch, linkable],
  );

  useEffect(() => {
    return () => unsubscribeRef.current && unsubscribeRef.current(name);
    // On unmount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valueState = link?.nationalId
    ? 'externalVerified'
    : (link?.linkStatus ?? 'empty');

  const searching = !readonly && sourceField?.fieldId === name;
  const iconColor = searching
    ? 'primary'
    : error === true
      ? 'error'
      : error === 'warning'
        ? 'warning'
        : 'primary';
  const className = searching
    ? 'LinkedField-searching'
    : error === true
      ? 'LinkedField-error'
      : error === 'warning'
        ? 'LinkedField-warning'
        : undefined;

  const canEdit =
    !!onEdit &&
    !readonly &&
    (valueState === 'empty' ||
      valueState === 'unverified' ||
      valueState === 'unnamed');

  const endAdornmentActions: React.JSX.Element = useMemo(() => {
    const EditAction =
      canEdit && link?.linkStatus !== 'unnamed' ? (
        <InputAdornment position="end">
          <IconButton
            data-cy={'button-edit'}
            color="primary"
            size="small"
            onClick={handleToggleDirectEditMode}>
            <Icon icon={isDirectEditMode ? 'CancelEdit' : 'Edit'} />
          </IconButton>
        </InputAdornment>
      ) : null;

    const ShowMoreAction =
      formattedValue && formattedValue.length > 0 ? (
        <Tooltip title={formattedValue} followCursor>
          <span
            style={{
              marginTop: 6,
              cursor: 'help',
            }}>
            <Icon icon={'InfoCircle'} />
          </span>
        </Tooltip>
      ) : null;

    return (
      <InputAdornment position="end">
        {EditAction}
        {ShowMoreAction}
      </InputAdornment>
    );
  }, [
    canEdit,
    formattedValue,
    handleToggleDirectEditMode,
    isDirectEditMode,
    link?.linkStatus,
  ]);

  return (
    <Layout {...layout} sx={{flexDirection: 'row'}}>
      {label && (
        <FieldLabel
          label={label}
          required={required}
          error={error}
          htmlFor={name}
        />
      )}
      {readonly && !link && !showWhenReadonlyAndEmpty ? (
        <NullFieldPlaceholder />
      ) : (
        <MuiTextFieldStyled
          inputRef={ref}
          required={required !== false}
          size="small"
          fullWidth
          id={name}
          name={name}
          readonly={readonly}
          value={formattedValue}
          className={className}
          type="text"
          onClick={isDirectEditMode ? undefined : handleSearch}
          onChange={handleDirectChange}
          onBlur={onBlur}
          slotProps={{
            input: {
              id: focusableId,
              readOnly: readonly || !isDirectEditMode,
              onKeyDown: isDirectEditMode ? undefined : handleKeyDown,
              startAdornment: (
                <InputAdornment position="start" sx={{cursor: 'pointer'}}>
                  <LinkStatusIcon status={valueState} color={iconColor} />
                </InputAdornment>
              ),
              endAdornment: endAdornmentActions,
            },
          }}
          {...props}
        />
      )}
    </Layout>
  );
};
